import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Clock, ShieldCheck, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import SeoHead from "../components/SeoHead";
import ServiceCard from "../components/ServiceCard";
import { getLocality } from "../data/services";
import { useServices } from "../context/ServicesContext";
import { STATS } from "../data/content";

export default function LocationPage() {
  const { locality } = useParams();
  const navigate = useNavigate();
  const loc = getLocality(locality);
  const { services } = useServices();

  if (!loc) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <h1 className="font-display text-2xl font-bold text-slate-900">Area not found</h1>
        <Button onClick={() => navigate("/")} className="mt-4 bg-brand-orange hover:bg-orange-600 text-white">Back to Home</Button>
      </div>
    );
  }

  const top = services.slice(0, 8);

  return (
    <div>
      <SeoHead
        title={`Home Services in ${loc.name} | AC Repair, Electrician, Plumber - ApnaFix`}
        description={`Find verified local professionals in ${loc.name}. Book AC repair, electrician, plumber & home services near ${loc.areas}.`}
      />
      <div className="bg-navy text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" /> Home</Link>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/20 text-emerald-300 px-3 py-1 text-xs font-bold"><MapPin className="w-3.5 h-3.5" /> Serving {loc.societies} in {loc.name}</span>
          <h1 className="mt-3 font-display text-3xl sm:text-5xl font-extrabold tracking-tight">Local Home Services in {loc.name}</h1>
          <p className="mt-3 text-slate-300 max-w-xl">Verified professionals available near {loc.areas}. Quick arrival, transparent pricing, trusted by your neighbours.</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Button data-testid="location-book-btn" onClick={() => navigate(`/book?locality=${encodeURIComponent(loc.name)}`)} className="bg-brand-orange hover:bg-orange-600 text-white font-bold h-12 px-6">Book a Service <ArrowRight className="w-4 h-4 ml-1" /></Button>
            <span className="inline-flex items-center gap-2 text-sm text-slate-300"><Clock className="w-4 h-4 text-orange-300" /> ~21 min avg. arrival in {loc.name}</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {STATS.map((s) => (
            <div key={s.label} className="rounded-2xl border border-slate-200 bg-white p-5 text-center">
              <p className="font-mono-num text-2xl font-bold text-navy">{s.value}</p>
              <p className="text-xs text-slate-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900">Popular services in {loc.name}</h2>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {top.map((s, i) => <ServiceCard key={s.id} service={s} index={i} />)}
        </div>

        <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 flex items-start gap-3">
          <ShieldCheck className="w-6 h-6 text-emerald-600 shrink-0" />
          <p className="text-sm text-slate-600">Every ApnaFix professional serving {loc.name} is background-checked and skill-verified, so you can book with confidence.</p>
        </div>
      </div>
    </div>
  );
}
